import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Box,
  IconButton,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Popover,
  Chip,
} from '@mui/material';
import {
  GetApp,
  EventAvailable,
  WhatsApp,
} from '@mui/icons-material';
import TopBar from '../components/TopBar';
import * as XLSX from 'xlsx';
import { patientService, appointmentService, userService, authService, prescriptionService } from '../services/api';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const EDDCalendarPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const today = new Date();

  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openApptDialog, setOpenApptDialog] = useState(false);
  const [apptForm, setApptForm] = useState({
    patientId: '',
    doctorId: '',
    appointmentDate: '',
    appointmentTime: '',
    type: 'OPD',
    notes: '',
    status: 'Scheduled',
    bp: '',
    pulse: '',
    weight: '',
    spO2: '',
    gestationalAge: '',
  });
  const [lastDiagnosis, setLastDiagnosis] = useState(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        patientService.getAllPatients(),
        userService.getDoctors(),
      ]);
      setPatients(pRes.data);
      setDoctors(dRes.data);
    } catch (err) {
      setError('Failed to load data');
    }
  };

  const getPatientsForDay = (day) =>
    patients.filter(p => {
      if (!p.edd) return false;
      const d = new Date(p.edd);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth && d.getDate() === day;
    });

  const getMonthDeliveryCount = () =>
    patients.filter(p => {
      if (!p.edd) return false;
      const d = new Date(p.edd);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    }).length;

  const getDaysInMonth = () => new Date(currentYear, currentMonth + 1, 0).getDate();
  const getFirstDayOfMonth = () => new Date(currentYear, currentMonth, 1).getDay();

  const calculateGestationalAge = (lmp) => {
    if (!lmp) return '';
    const diffDays = Math.ceil(Math.abs(new Date() - new Date(lmp)) / 86400000);
    return `${Math.floor(diffDays / 7)}w ${diffDays % 7}d`;
  };

  const handlePatientClick = async (e, patient) => {
    setPopoverAnchor(e.currentTarget);
    setSelectedPatient(patient);
    setLastDiagnosis(null);
    setDiagnosisLoading(true);
    try {
      const res = await prescriptionService.getLastPrescription(patient.id);
      setLastDiagnosis(res.data?.provisionalDiagnosis || null);
    } catch {
      setLastDiagnosis(null);
    } finally {
      setDiagnosisLoading(false);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedPatient(null);
    setLastDiagnosis(null);
  };

  const handleCreateAppointment = () => {
    const patient = selectedPatient;
    handlePopoverClose();
    setApptForm({
      patientId: patient.id,
      doctorId: '',
      appointmentDate: patient.edd ? patient.edd.split('T')[0] : '',
      appointmentTime: '',
      type: 'OPD',
      notes: '',
      status: 'Scheduled',
      bp: '',
      pulse: '',
      weight: '',
      spO2: '',
      gestationalAge: patient.lmp ? calculateGestationalAge(patient.lmp) : '',
    });
    setOpenApptDialog(true);
  };

  const handleWhatsApp = () => {
    const phone = selectedPatient.phoneNumber?.replace(/\D/g, '');
    const eddStr = selectedPatient.edd
      ? new Date(selectedPatient.edd).toLocaleDateString('en-IN')
      : '';
    const msg = encodeURIComponent(
      `Dear ${selectedPatient.firstName}, your Expected Delivery Date (EDD) is ${eddStr}. Please visit Vedanta Hospital for your scheduled appointment.`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
    handlePopoverClose();
  };

  const handleApptSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentService.createAppointment({
        ...apptForm,
        patientId: parseInt(apptForm.patientId),
        doctorId: parseInt(apptForm.doctorId),
      });
      setSuccess('Appointment created successfully!');
      setOpenApptDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create appointment');
      setTimeout(() => setError(''), 3000);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleExport = () => {
    const monthPatients = patients
      .filter(p => {
        if (!p.edd) return false;
        const d = new Date(p.edd);
        return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
      })
      .sort((a, b) => new Date(a.edd) - new Date(b.edd));

    const data = monthPatients.map(p => ({
      'Patient ID': p.id,
      'Patient Name': `${p.firstName} ${p.lastName}`,
      'Age': calculateAge(p.dateOfBirth),
      'EDD': p.edd ? new Date(p.edd).toLocaleDateString('en-IN') : '',
      'Mobile Number': p.phoneNumber || '',
      'Reminder Sent via Call': '',
      'Coming for Delivery': '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    ws['!cols'] = [
      { wch: 12 },  // Patient ID
      { wch: 25 },  // Patient Name
      { wch: 8 },   // Age
      { wch: 14 },  // EDD
      { wch: 18 },  // Mobile Number
      { wch: 24 },  // Reminder Sent via Call
      { wch: 22 },  // Coming for Delivery
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `EDD ${MONTH_NAMES[currentMonth]} ${currentYear}`);
    XLSX.writeFile(wb, `EDD_${MONTH_NAMES[currentMonth]}_${currentYear}.xlsx`);
  };

  const daysInMonth = getDaysInMonth();
  const firstDay = getFirstDayOfMonth();
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
  const yearOptions = Array.from({ length: 6 }, (_, i) => today.getFullYear() - 2 + i);

  const getPatientDisplayName = (id) => {
    const p = patients.find(pt => pt.id === id);
    return p ? `${p.firstName} ${p.lastName}` : '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="OBG - EDD Calendar" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {/* Top row: Year | Title + badge | Export */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TextField
              select
              size="small"
              label="Year"
              value={currentYear}
              onChange={(e) => setCurrentYear(Number(e.target.value))}
              sx={{ width: 120 }}
            >
              {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>

            <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                {MONTH_NAMES[currentMonth]} {currentYear}
              </Typography>
              <Chip
                label={`${getMonthDeliveryCount()} Deliveries`}
                color="success"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Button
              variant="contained"
              color="success"
              startIcon={<GetApp />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Box>

          {/* Month selector tabs */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 3, flexWrap: 'wrap' }}>
            {MONTHS.map((m, i) => (
              <Button
                key={m}
                size="small"
                variant={i === currentMonth ? 'contained' : 'outlined'}
                onClick={() => setCurrentMonth(i)}
                sx={{ minWidth: 48, px: 1, fontWeight: i === currentMonth ? 700 : 400 }}
              >
                {m}
              </Button>
            ))}
          </Box>

          {/* Calendar grid */}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            {/* Day name headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: '#e3f2fd' }}>
              {DAY_NAMES.map((d, i) => (
                <Box
                  key={d}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    borderRight: i < 6 ? '1px solid #bbdefb' : 0,
                  }}
                >
                  {d}
                </Box>
              ))}
            </Box>

            {/* Date cells */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {Array.from({ length: totalCells }, (_, i) => {
                const day = i - firstDay + 1;
                const isValid = day >= 1 && day <= daysInMonth;
                const isToday =
                  isValid &&
                  today.getFullYear() === currentYear &&
                  today.getMonth() === currentMonth &&
                  today.getDate() === day;
                const dayPatients = isValid ? getPatientsForDay(day) : [];

                return (
                  <Box
                    key={i}
                    sx={{
                      minHeight: 100,
                      p: 0.75,
                      borderRight: (i + 1) % 7 !== 0 ? '1px solid #e0e0e0' : 0,
                      borderBottom: '1px solid #e0e0e0',
                      bgcolor: !isValid ? '#f5f5f5' : isToday ? '#e3f2fd' : 'white',
                    }}
                  >
                    {isValid && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.5, fontWeight: isToday ? 700 : 400, color: isToday ? '#1976d2' : 'inherit' }}
                        >
                          {day}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                          {dayPatients.map(p => (
                            <Chip
                              key={p.id}
                              label={`${p.firstName} ${p.lastName}`}
                              size="small"
                              onClick={(e) => handlePatientClick(e, p)}
                              sx={{
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                fontSize: '0.68rem',
                                height: 22,
                                cursor: 'pointer',
                                '& .MuiChip-label': { px: 0.8 },
                                '&:hover': { bgcolor: '#c8e6c9' },
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, bgcolor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">Today</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="Patient"
                size="small"
                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', height: 20, fontSize: '0.68rem' }}
              />
              <Typography variant="caption" color="text.secondary">
                Expected Delivery Date (Click for actions)
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Patient Actions Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 4 } }}
      >
        {selectedPatient && (
          <Box sx={{ p: 2, minWidth: 240 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {selectedPatient.firstName} {selectedPatient.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              EDD: {selectedPatient.edd ? new Date(selectedPatient.edd).toLocaleDateString('en-IN') : '-'}
              {' | '}Phone: {selectedPatient.phoneNumber || '-'}
            </Typography>
            <Box sx={{ mb: 1.5, minHeight: 24 }}>
              {diagnosisLoading ? (
                <Typography variant="caption" color="text.disabled">Loading diagnosis...</Typography>
              ) : lastDiagnosis ? (
                <Typography variant="caption" sx={{ display: 'block', color: '#5c35a0', fontStyle: 'italic' }}>
                  Dx: {lastDiagnosis}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.disabled">No diagnosis on record</Typography>
              )}
            </Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EventAvailable />}
              onClick={handleCreateAppointment}
              sx={{ mb: 1 }}
            >
              Create Appointment
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<WhatsApp />}
              onClick={handleWhatsApp}
            >
              Send WhatsApp
            </Button>
          </Box>
        )}
      </Popover>

      {/* Create Appointment Dialog */}
      <Dialog open={openApptDialog} onClose={() => setOpenApptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Appointment</DialogTitle>
        <form onSubmit={handleApptSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Patient"
                  value={getPatientDisplayName(apptForm.patientId)}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Doctor"
                  value={apptForm.doctorId}
                  onChange={(e) => setApptForm({ ...apptForm, doctorId: e.target.value })}
                  required
                >
                  <MenuItem value="">Select Doctor</MenuItem>
                  {doctors.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      Dr. {d.firstName} {d.lastName}
                      {d.specialization ? ` - ${d.specialization}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Appointment Date"
                  type="date"
                  value={apptForm.appointmentDate}
                  onChange={(e) => setApptForm({ ...apptForm, appointmentDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Appointment Time"
                  type="time"
                  value={apptForm.appointmentTime}
                  onChange={(e) => setApptForm({ ...apptForm, appointmentTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={apptForm.type}
                  onChange={(e) => setApptForm({ ...apptForm, type: e.target.value })}
                >
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="IPD">IPD</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                  <MenuItem value="Follow-up">Follow-up</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gestational Age"
                  value={apptForm.gestationalAge}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated from patient's LMP"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={apptForm.notes}
                  onChange={(e) => setApptForm({ ...apptForm, notes: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenApptDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Create Appointment</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default EDDCalendarPage;
